import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import {
  getActiveAnnouncements,
  ActiveAnnouncement,
} from '../services/notifications'

// Map URL paths to view types for filtering announcements
const getViewTypeFromPath = (pathname: string): string[] => {
  if (pathname.includes('/shipment-management/merch-vendor')) {
    return ['VENDOR']
  } else if (pathname.includes('/shipment-management/consolidator')) {
    return ['CONSOL']
  } else if (pathname.includes('/shipment-management/reverse-logistics')) {
    return ['REVERSE']
  }
  // For other paths like transportation-profile, return all view types
  return ['VENDOR', 'CONSOL', 'REVERSE']
}

export const useAnnouncementNotifications = () => {
  const location = useLocation()

  const {
    data: announcements = [],
    isLoading,
    error,
    refetch,
  } = useQuery(['activeAnnouncements'], getActiveAnnouncements, {
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to tab (good for announcements)
    refetchOnReconnect: true, // Refresh when network reconnects
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  })

  // Get current view types based on URL
  const currentViewTypes = getViewTypeFromPath(location.pathname)

  // Filter announcements for current view
  const currentViewAnnouncements = announcements.filter(
    (announcement: ActiveAnnouncement) =>
      announcement.view_types.some((viewType: string) =>
        currentViewTypes.includes(viewType),
      ),
  )

  // Check if each navigation section has announcements
  const navigationNotifications = {
    shipmentManagement: {
      merch: announcements.filter((a: ActiveAnnouncement) =>
        a.view_types.includes('VENDOR'),
      ),
      consolidator: announcements.filter((a: ActiveAnnouncement) =>
        a.view_types.includes('CONSOL'),
      ),
      reverse: announcements.filter((a: ActiveAnnouncement) =>
        a.view_types.includes('REVERSE'),
      ),
    },
    total: announcements.length,
  }

  return {
    announcements,
    currentViewAnnouncements,
    navigationNotifications,
    isLoading,
    error,
    refetch, // Manual refetch function
    hasCurrentViewAnnouncements: currentViewAnnouncements.length > 0,
    hasAnyAnnouncements: announcements.length > 0,
  }
}
